import React, { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../../../auth/AuthContext';
import { appDocumentTitle } from '../../../../appMeta';
import { useDocumentTitle } from '../../../../hooks/useDocumentTitle';
import {
  UserPermissionPresetEditor,
  type UserPermissionPresetEditorHandle,
} from '../../../../components/UserPermissionPresetEditor';
import {
  useUpdateManagedUserMutation,
  useMasterEntityMiniListQuery,
  useUserDetailQuery,
  useUserTypesQuery,
} from '../../../../query/hooks';

const ADMIN_ANALIS_LAB_TYPE_CODE = 'admin_analis_lab';

function userTypeCodeForId(
  userTypes: { numericId: number | null; type_code: string }[],
  userTypeId: string,
): string {
  const row = userTypes.find((t) => t.numericId != null && String(t.numericId) === userTypeId);
  return row?.type_code?.trim().toLowerCase() ?? '';
}

function analisTypeDataOptions(rows: Array<Record<string, unknown>> | undefined) {
  return (rows ?? [])
    .map((row) => {
      const id = Number(row.id);
      const title = String(row.title ?? '').trim();
      if (!Number.isInteger(id) || id < 1 || !title) return null;
      return { id: String(id), title };
    })
    .filter((row): row is { id: string; title: string } => row != null)
    .sort((a, b) => a.title.localeCompare(b.title, 'id'));
}

export type UserDetailMode = 'edit' | 'view';

type DetailTabKey = 'user' | 'preset';

/** Edit or view single user (`/superadmin/users/:id/...` where `id` is the Strapi integer user PK). */
export function UserDetailTabScreen({ mode }: { mode: UserDetailMode }) {
  const { id: idParam } = useParams<{ id: string }>();
  const id = idParam ? decodeURIComponent(idParam) : null;
  const { jwt } = useAuth();
  const navigate = useNavigate();

  useDocumentTitle(
    appDocumentTitle(mode === 'edit' ? 'Edit user' : 'View user'),
  );

  const { data: user, isPending, isError, error, refetch } = useUserDetailQuery(jwt, id);
  const { data: userTypes = [], isPending: typesPending } = useUserTypesQuery(jwt);
  const { data: analisTypeRows = [], isPending: analisTypesPending } = useMasterEntityMiniListQuery(
    jwt,
    'analis-type-datas',
    Boolean(jwt),
  );
  const analisTypeOptions = analisTypeDataOptions(analisTypeRows);
  const updateMutation = useUpdateManagedUserMutation(jwt);
  const presetEditorRef = useRef<UserPermissionPresetEditorHandle | null>(null);

  const readOnly = mode === 'view';

  const [activeTab, setActiveTab] = useState<DetailTabKey>('user');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [blocked, setBlocked] = useState(false);
  /** Integer PK of `user-types` row for `/api/users` (not Content-API `documentId`). */
  const [userTypeId, setUserTypeId] = useState('');
  const [analisTypeDataIds, setAnalisTypeDataIds] = useState<string[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);
  const selectedTypeCode = userTypeCodeForId(userTypes, userTypeId);
  const isAdminAnalisLab =
    selectedTypeCode === ADMIN_ANALIS_LAB_TYPE_CODE ||
    (selectedTypeCode === '' &&
      user?.userTypeCode?.trim().toLowerCase() === ADMIN_ANALIS_LAB_TYPE_CODE &&
      userTypeId === (user.userTypeId ?? ''));

  useEffect(() => {
    if (!user) return;
    setUsername(user.username);
    setBlocked(user.blocked);
    setPassword('');
    setUserTypeId(user.userTypeId ?? '');
    setAnalisTypeDataIds(user.analisTypeDataIds ?? []);
  }, [user]);

  async function saveUserTab(closeAfterSave: boolean) {
    if (!jwt || !id || readOnly || !user) return;
    if (!userTypeId.trim()) return;
    try {
      await updateMutation.mutateAsync({
        id,
        username,
        blocked,
        password: password.trim() === '' ? null : password,
        userTypeId: userTypeId.trim(),
        analisTypeDataIds: isAdminAnalisLab ? analisTypeDataIds : [],
      });
      await refetch();
      setPassword('');
      if (closeAfterSave) {
        navigate('/superadmin/users', {
          state: { snackbarMessage: 'User berhasil diedit.' },
        });
        return;
      }
      setSnackbarMessage('User berhasil diedit.');
    } catch {
      /* surfaced below */
    }
  }

  async function savePresetTab() {
    if (!jwt || !id || readOnly || !user) return;
    try {
      await presetEditorRef.current?.save();
      await refetch();
      setSnackbarMessage('Permission preset berhasil disimpan.');
    } catch {
      /* surfaced in editor */
    }
  }

  function onCancel() {
    navigate('/superadmin/users');
  }

  const userSaveDisabled =
    updateMutation.isPending || username.trim().length < 3 || !userTypeId.trim();

  if (!id || !Number.isFinite(Number(id))) {
    return (
      <Box>
        <Alert severity="warning">Missing or invalid user id (numeric id required).</Alert>
        <Button component={RouterLink} to="/superadmin/users" sx={{ mt: 2 }}>
          Back to users
        </Button>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      {isPending && (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={32} />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error instanceof Error ? error.message : 'Failed to load user.'}
        </Alert>
      )}

      {user && !isPending ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          <Box sx={{ px: 1.5, pt: 1, pb: 0 }}>
            <Tabs value={activeTab} onChange={(_, v: DetailTabKey) => setActiveTab(v)} sx={{ mb: 0 }}>
              <Tab label="User details" value="user" />
              <Tab label="Permission preset" value="preset" />
            </Tabs>
          </Box>

          <Box sx={{ px: 1.5, py: 1.5, flex: 1, minHeight: 0, overflow: 'auto' }}>
            {activeTab === 'user' && (
              <Stack spacing={2} sx={{ maxWidth: 440 }}>
                <TextField
                  label="Username"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={readOnly}
                  required
                  fullWidth
                />
                <TextField label="Email" value={user.email} disabled fullWidth />
                {!readOnly && (
                  <TextField
                    label="New password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    fullWidth
                    autoComplete="new-password"
                    helperText="Optional. Minimum 6 characters when set."
                    inputProps={{ minLength: 6 }}
                  />
                )}
                {readOnly ? (
                  <TextField
                    label="User type"
                    value={
                      user.userTypeCode
                        ? `${user.userTypeCode}${user.userTypeName ? ` — ${user.userTypeName}` : ''}`
                        : '—'
                    }
                    disabled
                    fullWidth
                  />
                ) : (
                  <FormControl disabled={typesPending} size="small" fullWidth required>
                    <InputLabel id="user-type-label">User type</InputLabel>
                    <Select
                      labelId="user-type-label"
                      label="User type"
                      value={userTypeId}
                      onChange={(e) => {
                        const next = String(e.target.value);
                        setUserTypeId(next);
                        if (userTypeCodeForId(userTypes, next) !== ADMIN_ANALIS_LAB_TYPE_CODE) {
                          setAnalisTypeDataIds([]);
                        }
                      }}
                      displayEmpty
                      renderValue={(v) => {
                        if (!v) return <em>Select user type</em>;
                        const row = userTypes.find((t) => t.numericId != null && String(t.numericId) === v);
                        return row
                          ? `${row.type_code}${row.type_name ? ` — ${row.type_name}` : ''}`
                          : v;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select user type</em>
                      </MenuItem>
                      {userTypes
                        .filter((t) => t.numericId != null)
                        .map((t) => (
                          <MenuItem key={t.numericId} value={String(t.numericId)}>
                            {t.type_code}
                            {t.type_name ? ` — ${t.type_name}` : ''}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                )}
                {isAdminAnalisLab &&
                  (readOnly ? (
                    <TextField
                      label="Analis type"
                      value={user.analisTypeDataTitles.join(', ') || '—'}
                      disabled
                      fullWidth
                    />
                  ) : (
                    <FormControl disabled={analisTypesPending} size="small" fullWidth>
                      <InputLabel id="analis-type-label">Analis type</InputLabel>
                      <Select
                        labelId="analis-type-label"
                        label="Analis type"
                        multiple
                        value={analisTypeDataIds}
                        onChange={(e) => {
                          const value = e.target.value;
                          setAnalisTypeDataIds(typeof value === 'string' ? value.split(',') : value);
                        }}
                        displayEmpty
                        data-testid="user-detail-analis-type"
                        renderValue={(selected) => {
                          if (selected.length === 0) return <em>Select analis type</em>;
                          return selected
                            .map((id) => {
                              const fromOptions = analisTypeOptions.find((row) => row.id === id)?.title;
                              if (fromOptions) return fromOptions;
                              const savedIndex = user.analisTypeDataIds.indexOf(id);
                              return savedIndex >= 0 ? user.analisTypeDataTitles[savedIndex] : id;
                            })
                            .filter(Boolean)
                            .join(', ');
                        }}
                      >
                        {analisTypeOptions.map((row) => (
                          <MenuItem key={row.id} value={row.id}>
                            {row.title}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ))}
                <FormControl disabled={readOnly} size="small" fullWidth>
                  <InputLabel id="acct-status-label">Account</InputLabel>
                  <Select
                    labelId="acct-status-label"
                    label="Account"
                    value={blocked ? 'inactive' : 'active'}
                    onChange={(e) => setBlocked(e.target.value === 'inactive')}
                  >
                    <MenuItem value="active">Active</MenuItem>
                    <MenuItem value="inactive">Inactive (blocked)</MenuItem>
                  </Select>
                </FormControl>
                {updateMutation.isError && activeTab === 'user' && (
                  <Typography variant="body2" color="error">
                    {updateMutation.error instanceof Error
                      ? updateMutation.error.message
                      : 'Unable to save.'}
                  </Typography>
                )}
              </Stack>
            )}

            {activeTab === 'preset' && (
              <Stack spacing={2} sx={{ maxWidth: 560 }}>
                <UserPermissionPresetEditor
                  ref={presetEditorRef}
                  jwt={jwt}
                  userId={id}
                  readOnly={readOnly}
                />
              </Stack>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'flex-start',
              alignItems: 'center',
              gap: 1,
              px: 1.5,
              py: 1,
              flexShrink: 0,
              borderTop: 1,
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Button variant="outlined" color="inherit" onClick={onCancel}>
              Cancel
            </Button>
            {!readOnly && activeTab === 'user' && (
              <>
                <Button
                  variant="contained"
                  disabled={userSaveDisabled}
                  onClick={() => void saveUserTab(false)}
                >
                  Save
                </Button>
                <Button
                  variant="contained"
                  disabled={userSaveDisabled}
                  onClick={() => void saveUserTab(true)}
                >
                  Save and close
                </Button>
              </>
            )}
            {!readOnly && activeTab === 'preset' && (
              <Button variant="contained" onClick={() => void savePresetTab()}>
                Save permission preset
              </Button>
            )}
          </Box>
        </Box>
      ) : null}

      <Snackbar
        open={Boolean(snackbarMessage)}
        autoHideDuration={4000}
        onClose={() => setSnackbarMessage(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ mt: 1, mr: 1 }}
      >
        <Alert
          severity="success"
          variant="filled"
          onClose={() => setSnackbarMessage(null)}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
